import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Play, ExternalLink, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BASE, call, nz, JOB_TYPES, Pill } from "./common";
import ImportDialog from "./ImportDialog";
import HistoryPanel from "./HistoryPanel";
import SettingsPanel from "./SettingsPanel";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border bg-white p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 font-bold text-steel">{value}</div>
  </div>
);

export default function SupplierPage({ s, reload }) {
  const [importing, setImporting] = useState(false);
  const [jtype, setJtype] = useState("stock");
  const [bump, setBump] = useState(0);
  const run = async () => {
    const d = await call(() => api.post(`${BASE}/suppliers/${s.id}/run`, { type: jtype }));
    if (d) { if (d.duplicate) toast.info("That job is already queued or running"); else toast.success("Queued — it runs as soon as the runner is free"); setBump((b) => b + 1); }
  };
  return (
    <div className="space-y-6" data-testid={`supplier-page-${s.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold flex items-center gap-3">{s.name} <Pill s={s.status === "active" ? "completed" : "cancelled"} testid="supplier-status" /></h2>
          {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-sm text-accent hover:underline inline-flex items-center gap-1" data-testid="supplier-website">{s.website} <ExternalLink size={13} /></a>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setImporting(true)} className="bg-accent text-steel hover:bg-amber-600 font-semibold" data-testid="supplier-import-csv-btn"><Upload size={16} className="mr-1" />Import CSV</Button>
          <Select value={jtype} onValueChange={setJtype}>
            <SelectTrigger className="w-48" data-testid="supplier-run-type"><SelectValue /></SelectTrigger>
            <SelectContent>{JOB_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={run} data-testid="supplier-run-sync-btn"><Play size={15} className="mr-1" />Run synchronisation</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Products" value={s.product_count.toLocaleString()} testid="supplier-product-count" />
        <Stat label="Last import" value={nz(s.last_import_at)} testid="supplier-last-import" />
        <Stat label="Last stock sync" value={nz(s.last_stock_sync_at)} testid="supplier-last-stock" />
        <Stat label="Last price sync" value={nz(s.last_price_sync_at)} testid="supplier-last-price" />
        <Stat label="Last scraper run" value={s.last_job ? <span className="flex items-center gap-2">{nz(s.last_job.finished_at)} <Pill s={s.last_job.status} /></span> : "—"} testid="supplier-last-job" />
        <Stat label="Next scheduled run" value={nz(s.next_run_at)} testid="supplier-next-run" />
        <Stat label="Data source" value={s.feed_url ? "Feed URL" : "CSV uploads"} testid="supplier-source" />
        <Stat label="Media" value={<span className="flex items-center gap-1 text-sm"><ImageIcon size={14} /> External URLs only</span>} testid="supplier-media" />
      </div>
      <HistoryPanel s={s} bump={bump} />
      <SettingsPanel s={s} reload={reload} />
      <ImportDialog s={s} open={importing} onClose={() => setImporting(false)} onQueued={() => { setImporting(false); setBump((b) => b + 1); reload(); }} />
    </div>
  );
}
